// SdsDetailPanel.js
// Read-only summary of GET /api/sds/item. Every block renders only when it has content;
// the field set is provisional until the phase-0 fixtures land (finalised in phase 6).

import React from "react";
import { buildSdsDownloadUrl } from "../../../api/SdsApi";
import styles from "./SdsLookupSection.module.css";

const hasItems = (list) => Array.isArray(list) && list.length > 0;

const hasGridValues = (grid) =>
  !!grid && Object.values(grid).some((value) => value !== null && value !== undefined && value !== "");

const CodeList = ({ title, codes }) => {
  if (!hasItems(codes)) return null;
  return (
    <div className={styles.detailBlock}>
      <div className={styles.detailLabel}>{title}</div>
      <ul className={styles.codeList}>
        {codes.map((entry, index) => (
          <li key={`${entry.code || "code"}-${index}`}>
            {entry.code && <strong>{entry.code}</strong>}
            {entry.code && entry.text ? " " : ""}
            {entry.text}
          </li>
        ))}
      </ul>
    </div>
  );
};

const RatingGrid = ({ title, grid }) => {
  if (!hasGridValues(grid)) return null;
  const entries = Object.entries(grid).filter(
    ([, value]) => value !== null && value !== undefined && value !== ""
  );
  return (
    <div className={styles.detailBlock}>
      <div className={styles.detailLabel}>{title}</div>
      <div className={styles.ratingGrid}>
        {entries.map(([key, value]) => (
          <div key={key} className={styles.ratingCell}>
            <span className={styles.ratingKey}>{key}</span>
            <span className={styles.ratingValue}>{String(value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const TextDisclosure = ({ title, entries }) => {
  if (!hasItems(entries)) return null;
  return (
    <details className={styles.disclosure}>
      <summary>{title}</summary>
      <ul className={styles.textList}>
        {entries.map((entry, index) => (
          <li key={`${title}-${index}`}>
            {entry.title && <strong>{entry.title}: </strong>}
            {entry.text}
          </li>
        ))}
      </ul>
    </details>
  );
};

const HandlingDisclosure = ({ entries }) => {
  if (!hasItems(entries)) return null;
  return (
    <details className={styles.disclosure}>
      <summary>Handling and storage</summary>
      <ul className={styles.textList}>
        {entries.map((entry, index) => (
          <li key={`handling-${index}`}>
            {entry.handling && <div><strong>Handling: </strong>{entry.handling}</div>}
            {entry.storage && <div><strong>Storage: </strong>{entry.storage}</div>}
            {entry.other && <div>{entry.other}</div>}
          </li>
        ))}
      </ul>
    </details>
  );
};

const formatPercent = (ingredient) => {
  if (ingredient.percent !== null && ingredient.percent !== undefined) return String(ingredient.percent);
  const min = ingredient.min_percent;
  const max = ingredient.max_percent;
  if (min !== null && min !== undefined && max !== null && max !== undefined) return `${min} - ${max}`;
  return min ?? max ?? "";
};

const DocumentLinks = ({ title, docs, detail }) => {
  if (!hasItems(docs)) return null;
  return (
    <div className={styles.detailBlock}>
      <div className={styles.detailLabel}>{title}</div>
      <ul className={styles.documentList}>
        {docs.map((doc, index) => {
          const label = [doc.document_type, doc.country, doc.revision_date]
            .filter(Boolean)
            .join(" / ");
          return (
            <li key={`${doc.elink || "doc"}-${index}`} className={styles.documentRow}>
              <span>{label || "Document"}</span>
              {doc.elink && (
                <span className={styles.documentLinks}>
                  <a
                    className={styles.textLink}
                    href={doc.elink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View
                  </a>
                  <a
                    className={styles.textLink}
                    href={buildSdsDownloadUrl({
                      document_link: doc.elink,
                      product_name: detail.product_name,
                      manufacturer_name: detail.manufacturer_name,
                      revision_date: doc.revision_date,
                    })}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download
                  </a>
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const SdsDetailPanel = ({ detail }) => {
  if (!detail) return null;
  const hazardLine = [detail.signal_word, detail.main_hazard].filter(Boolean).join(" - ");
  const flashPoint = detail.flash_point;
  const flashPointText =
    flashPoint && flashPoint.value !== null && flashPoint.value !== undefined
      ? `${flashPoint.value}${flashPoint.units ? ` ${flashPoint.units}` : ""}`
      : null;

  return (
    <div className={styles.detailPanel} data-testid="sds-detail-panel">
      {hazardLine && (
        <div className={styles.detailBlock}>
          <div className={styles.detailLabel}>Hazard</div>
          <div className={styles.hazardLine}>
            {detail.signal_word && (
              <span className={styles.signalWord}>{detail.signal_word}</span>
            )}
            {detail.main_hazard && <span>{detail.main_hazard}</span>}
          </div>
        </div>
      )}

      {hasItems(detail.pictograms) && (
        <div className={styles.detailBlock}>
          <div className={styles.detailLabel}>Pictograms</div>
          <div className={styles.chipRow}>
            {detail.pictograms.filter(Boolean).map((code, index) => (
              <span key={`${code}-${index}`} className={styles.chip}>
                {String(code)}
              </span>
            ))}
          </div>
        </div>
      )}

      <CodeList title="Hazard statements" codes={detail.h_codes} />
      <CodeList title="Precautionary statements" codes={detail.p_codes} />

      <RatingGrid title="NFPA" grid={detail.nfpa} />
      <RatingGrid title="HMIS" grid={detail.hmis} />

      {(detail.physical_state || flashPointText) && (
        <div className={styles.detailBlock}>
          <div className={styles.detailLabel}>Properties</div>
          <div>
            {detail.physical_state && <span>Physical state: {detail.physical_state}</span>}
            {detail.physical_state && flashPointText && <span> | </span>}
            {flashPointText && <span>Flash point: {flashPointText}</span>}
          </div>
        </div>
      )}

      <TextDisclosure title="First aid" entries={detail.first_aid} />
      <TextDisclosure title="PPE" entries={detail.ppe} />
      <HandlingDisclosure entries={detail.handling_and_storage} />

      {hasItems(detail.ingredients) && (
        <div className={styles.detailBlock}>
          <div className={styles.detailLabel}>Ingredients</div>
          <div className={styles.tableScroll}>
            <table className={styles.ingredientsTable}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>CAS</th>
                  <th>%</th>
                </tr>
              </thead>
              <tbody>
                {detail.ingredients.map((ingredient, index) => (
                  <tr key={`${ingredient.cas || ingredient.chemical_name || "row"}-${index}`}>
                    <td>{ingredient.chemical_name}</td>
                    <td>{ingredient.cas}</td>
                    <td>{formatPercent(ingredient)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <DocumentLinks title="Documents" docs={detail.documents} detail={detail} />
      <DocumentLinks
        title="Previous revisions"
        docs={detail.previous_revisions}
        detail={detail}
      />
    </div>
  );
};
